import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { AppShell } from "@/components/app-shell";
import { requireRole } from "@/lib/session";
import { useTeacherContext } from "@/lib/teacher-context";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Megaphone, MessageSquare, Pin, Send } from "lucide-react";

export const Route = createFileRoute("/teacher/messages")({
  ssr: false,
  beforeLoad: () => requireRole("teacher"),
  head: () => ({ meta: [{ title: "Communication — MAKAI" }] }),
  component: MessagesPage,
});

type Tab = "announcements" | "parents" | "discussions";

function MessagesPage() {
  const ctx = useTeacherContext();
  const [tab, setTab] = useState<Tab>("announcements");

  return (
    <AppShell role="teacher" title="Communication" subtitle="Announcements, parent messages and class discussions.">
      <div className="mb-4 inline-flex rounded-full border border-border bg-card p-1 text-sm shadow-soft">
        {[
          { id: "announcements", label: "Announcements", icon: Megaphone },
          { id: "parents", label: "Parents", icon: Send },
          { id: "discussions", label: "Discussions", icon: MessageSquare },
        ].map((t) => {
          const Icon = t.icon as any;
          return (
            <button key={t.id} onClick={() => setTab(t.id as Tab)}
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 font-semibold transition ${tab === t.id ? "bg-gradient-african text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
              <Icon className="h-4 w-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "announcements" && <Announcements ctx={ctx} />}
      {tab === "parents" && <ParentMessages ctx={ctx} />}
      {tab === "discussions" && <Discussions ctx={ctx} />}
    </AppShell>
  );
}

function Announcements({ ctx }: any) {
  const [items, setItems] = useState<any[]>([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<any>({ title: "", body: "", audience: "class", stream_id: "", pinned: false });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    if (!ctx.schoolId) return;
    const streamIds = ctx.streams.map((s: any) => s.id);
    let q = supabase.from("announcements").select("id,title,body,audience,pinned,created_at,stream_id,stream:streams(name,class:classes(name))").eq("school_id", ctx.schoolId);
    if (streamIds.length) q = q.or(`stream_id.in.(${streamIds.join(",")}),stream_id.is.null`);
    const { data } = await q.order("pinned", { ascending: false }).order("created_at", { ascending: false });
    setItems(data ?? []);
  };
  useEffect(() => { if (!ctx.loading) load(); }, [ctx.loading, ctx.schoolId, ctx.streams.length]);

  async function save() {
    if (!form.title || !form.body) { toast.error("Title and body required"); return; }
    setSaving(true);
    const { data: { user } } = await supabase.auth.getUser();
    const { error } = await supabase.from("announcements").insert({
      school_id: ctx.schoolId, author_user_id: user!.id,
      title: form.title, body: form.body, audience: form.audience,
      stream_id: form.stream_id || null, pinned: form.pinned,
    });
    setSaving(false);
    if (error) toast.error(error.message); else { toast.success("Posted"); setOpen(false); setForm({ title: "", body: "", audience: "class", stream_id: "", pinned: false }); load(); }
  }

  return (
    <>
      <div className="mb-3 flex justify-end"><Button onClick={() => setOpen(true)}><Plus className="mr-1 h-4 w-4" /> New announcement</Button></div>
      {items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">No announcements.</div>
      ) : (
        <div className="space-y-3">
          {items.map((a) => (
            <article key={a.id} className="rounded-2xl border border-border bg-card p-4 shadow-soft">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="flex items-center gap-2">
                    {a.pinned && <Pin className="h-4 w-4 text-gold-foreground" />}
                    <h3 className="font-display text-lg font-bold">{a.title}</h3>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {a.stream ? `${a.stream.class?.name} · ${a.stream.name}` : a.audience} · {new Date(a.created_at).toLocaleString()}
                  </div>
                </div>
              </div>
              <p className="mt-2 whitespace-pre-wrap text-sm">{a.body}</p>
            </article>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>New announcement</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <Input placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            <Textarea rows={4} placeholder="Message" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} />
            <div className="grid gap-3 sm:grid-cols-2">
              <select value={form.stream_id} onChange={(e) => setForm({ ...form, stream_id: e.target.value })} className="rounded-lg border border-input bg-background px-3 py-2 text-sm">
                <option value="">No specific class</option>
                {ctx.streams.map((s: any) => <option key={s.id} value={s.id}>{s.class_name} · {s.name}</option>)}
              </select>
              <select value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })} className="rounded-lg border border-input bg-background px-3 py-2 text-sm">
                <option value="class">Class learners</option>
                <option value="parents">Parents</option>
                <option value="school">School-wide</option>
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.pinned} onChange={(e) => setForm({ ...form, pinned: e.target.checked })} /> Pin to top</label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving}>{saving ? "Posting…" : "Post"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function ParentMessages({ ctx }: any) {
  const [threads, setThreads] = useState<any[]>([]);
  const [active, setActive] = useState<any | null>(null);
  const [messages, setMessages] = useState<any[]>([]);
  const [body, setBody] = useState("");
  const [composeOpen, setComposeOpen] = useState(false);

  useEffect(() => {
    if (!ctx.schoolId) return;
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      const { data } = await supabase
        .from("teacher_messages")
        .select("id, parent_user_id, learner_id, subject, body, sender, created_at, read_at, learner:learners(first_name,last_name)")
        .eq("school_id", ctx.schoolId).eq("teacher_user_id", user!.id)
        .order("created_at", { ascending: false });
      // group by parent+learner
      const map = new Map<string, any>();
      for (const m of data ?? []) {
        const k = `${m.parent_user_id}|${m.learner_id ?? ""}`;
        if (!map.has(k)) map.set(k, { key: k, parent_user_id: m.parent_user_id, learner: m.learner, learner_id: m.learner_id, last: m });
      }
      setThreads(Array.from(map.values()));
    })();
  }, [ctx.schoolId]);

  useEffect(() => {
    if (!active) return;
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      const { data } = await supabase.from("teacher_messages")
        .select("id, body, sender, created_at")
        .eq("teacher_user_id", user!.id).eq("parent_user_id", active.parent_user_id)
        .order("created_at", { ascending: true });
      setMessages(data ?? []);
    })();
  }, [active]);

  async function send() {
    if (!active || !body.trim()) return;
    const { data: { user } } = await supabase.auth.getUser();
    const { error } = await supabase.from("teacher_messages").insert({
      school_id: ctx.schoolId, teacher_user_id: user!.id, parent_user_id: active.parent_user_id,
      learner_id: active.learner_id, body, sender: "teacher",
    });
    if (error) { toast.error(error.message); return; }
    setBody("");
    setMessages((m) => [...m, { id: crypto.randomUUID(), body, sender: "teacher", created_at: new Date().toISOString() }]);
  }

  return (
    <div className="grid gap-3 md:grid-cols-[280px_1fr]">
      <aside className="rounded-2xl border border-border bg-card shadow-soft">
        <div className="border-b border-border p-3"><Button size="sm" className="w-full" onClick={() => setComposeOpen(true)}><Plus className="mr-1 h-4 w-4" />New message</Button></div>
        <ul className="max-h-[60vh] divide-y divide-border overflow-auto">
          {threads.length === 0 && <li className="p-4 text-sm text-muted-foreground">No conversations.</li>}
          {threads.map((t) => (
            <li key={t.key}>
              <button onClick={() => setActive(t)} className={`block w-full px-3 py-2 text-left text-sm hover:bg-muted/60 ${active?.key === t.key ? "bg-primary-soft" : ""}`}>
                <div className="font-semibold">{t.learner ? `${t.learner.first_name} ${t.learner.last_name}` : "General"}</div>
                <div className="truncate text-xs text-muted-foreground">{t.last?.body}</div>
              </button>
            </li>
          ))}
        </ul>
      </aside>
      <section className="rounded-2xl border border-border bg-card shadow-soft min-h-[60vh] flex flex-col">
        {active ? (
          <>
            <div className="flex-1 space-y-2 overflow-auto p-4">
              {messages.map((m) => (
                <div key={m.id} className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${m.sender === "teacher" ? "ml-auto bg-primary text-primary-foreground" : "bg-muted"}`}>
                  <div>{m.body}</div>
                  <div className="mt-1 text-[10px] opacity-70">{new Date(m.created_at).toLocaleString()}</div>
                </div>
              ))}
            </div>
            <div className="flex gap-2 border-t border-border p-3">
              <Input placeholder="Type your reply…" value={body} onChange={(e) => setBody(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") send(); }} />
              <Button onClick={send}><Send className="h-4 w-4" /></Button>
            </div>
          </>
        ) : <div className="grid flex-1 place-items-center text-sm text-muted-foreground">Select a conversation</div>}
      </section>

      <ComposeDialog open={composeOpen} onOpenChange={setComposeOpen} ctx={ctx} onSent={(t: any) => { setThreads((prev) => [t, ...prev]); setActive(t); }} />
    </div>
  );
}

function ComposeDialog({ open, onOpenChange, ctx, onSent }: any) {
  const [learners, setLearners] = useState<any[]>([]);
  const [learnerId, setLearnerId] = useState("");
  const [parents, setParents] = useState<any[]>([]);
  const [parentUserId, setParentUserId] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");

  useEffect(() => {
    if (!open || !ctx.streams.length) return;
    (async () => {
      const ids = ctx.streams.map((s: any) => s.id);
      const { data } = await supabase.from("learners").select("id, first_name, last_name").in("current_stream_id", ids).eq("status","active").order("first_name");
      setLearners(data ?? []);
    })();
  }, [open, ctx.streams]);

  useEffect(() => {
    if (!learnerId) { setParents([]); setParentUserId(""); return; }
    (async () => {
      const { data } = await supabase.from("parent_learner_links")
        .select("parent:parents(user_id, first_name, last_name)")
        .eq("learner_id", learnerId);
      const ps = (data ?? []).map((r: any) => r.parent).filter((p: any) => p?.user_id);
      setParents(ps); setParentUserId(ps[0]?.user_id ?? "");
    })();
  }, [learnerId]);

  async function send() {
    if (!parentUserId || !body) { toast.error("Pick a parent and write a message"); return; }
    const { data: { user } } = await supabase.auth.getUser();
    const { error } = await supabase.from("teacher_messages").insert({
      school_id: ctx.schoolId, teacher_user_id: user!.id, parent_user_id: parentUserId,
      learner_id: learnerId, subject, body, sender: "teacher",
    });
    if (error) { toast.error(error.message); return; }
    toast.success("Sent");
    const learner = learners.find((l) => l.id === learnerId);
    onSent({ key: `${parentUserId}|${learnerId}`, parent_user_id: parentUserId, learner, learner_id: learnerId, last: { body } });
    onOpenChange(false); setBody(""); setSubject("");
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>Message a parent</DialogTitle></DialogHeader>
        <div className="grid gap-3">
          <select value={learnerId} onChange={(e) => setLearnerId(e.target.value)} className="rounded-lg border border-input bg-background px-3 py-2 text-sm">
            <option value="">Choose learner…</option>
            {learners.map((l) => <option key={l.id} value={l.id}>{l.first_name} {l.last_name}</option>)}
          </select>
          <select value={parentUserId} onChange={(e) => setParentUserId(e.target.value)} disabled={!parents.length} className="rounded-lg border border-input bg-background px-3 py-2 text-sm">
            {parents.length === 0 ? <option value="">No linked parents</option> : parents.map((p) => <option key={p.user_id} value={p.user_id}>{p.first_name} {p.last_name}</option>)}
          </select>
          <Input placeholder="Subject (optional)" value={subject} onChange={(e) => setSubject(e.target.value)} />
          <Textarea rows={4} placeholder="Message" value={body} onChange={(e) => setBody(e.target.value)} />
        </div>
        <DialogFooter><Button onClick={send}><Send className="mr-1 h-4 w-4" /> Send</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Discussions({ ctx }: any) {
  const [threads, setThreads] = useState<any[]>([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", body: "", stream_id: "" });
  const [active, setActive] = useState<any | null>(null);
  const [posts, setPosts] = useState<any[]>([]);
  const [reply, setReply] = useState("");

  const load = async () => {
    if (!ctx.streams.length) return;
    const ids = ctx.streams.map((s: any) => s.id);
    const { data } = await supabase.from("discussion_threads")
      .select("id,title,pinned,locked,created_at,stream:streams(name,class:classes(name))")
      .in("stream_id", ids).order("pinned", { ascending: false }).order("created_at", { ascending: false });
    setThreads(data ?? []);
  };
  useEffect(() => { load(); }, [ctx.streams.length]);

  useEffect(() => {
    if (!active) return;
    (async () => {
      const { data } = await supabase.from("discussion_posts")
        .select("id, body, created_at, author_user_id").eq("thread_id", active.id).order("created_at", { ascending: true });
      setPosts(data ?? []);
    })();
  }, [active]);

  async function createThread() {
    if (!form.title || !form.body || !form.stream_id) { toast.error("Fill all fields"); return; }
    const { data: { user } } = await supabase.auth.getUser();
    const { data: th, error } = await supabase.from("discussion_threads").insert({
      school_id: ctx.schoolId, stream_id: form.stream_id, author_user_id: user!.id, title: form.title,
    }).select("id").single();
    if (error || !th) { toast.error(error?.message ?? "Failed"); return; }
    await supabase.from("discussion_posts").insert({ thread_id: th.id, school_id: ctx.schoolId, author_user_id: user!.id, body: form.body });
    toast.success("Thread created"); setOpen(false); setForm({ title: "", body: "", stream_id: "" }); load();
  }

  async function postReply() {
    if (!active || !reply.trim()) return;
    const { data: { user } } = await supabase.auth.getUser();
    const { error } = await supabase.from("discussion_posts").insert({
      thread_id: active.id, school_id: ctx.schoolId, author_user_id: user!.id, body: reply,
    });
    if (error) { toast.error(error.message); return; }
    setPosts((p) => [...p, { id: crypto.randomUUID(), body: reply, created_at: new Date().toISOString(), author_user_id: user!.id }]);
    setReply("");
  }

  return (
    <div className="grid gap-3 md:grid-cols-[300px_1fr]">
      <aside className="rounded-2xl border border-border bg-card shadow-soft">
        <div className="border-b border-border p-3"><Button size="sm" className="w-full" onClick={() => setOpen(true)}><Plus className="mr-1 h-4 w-4" />New thread</Button></div>
        <ul className="max-h-[60vh] divide-y divide-border overflow-auto">
          {threads.length === 0 && <li className="p-4 text-sm text-muted-foreground">No threads yet.</li>}
          {threads.map((t) => (
            <li key={t.id}>
              <button onClick={() => setActive(t)} className={`block w-full px-3 py-2 text-left text-sm hover:bg-muted/60 ${active?.id === t.id ? "bg-primary-soft" : ""}`}>
                <div className="flex items-center gap-1 font-semibold">{t.pinned && <Pin className="h-3 w-3 text-gold-foreground" />}{t.title}</div>
                <div className="text-xs text-muted-foreground">{t.stream?.class?.name} · {t.stream?.name}</div>
              </button>
            </li>
          ))}
        </ul>
      </aside>
      <section className="rounded-2xl border border-border bg-card shadow-soft min-h-[60vh] flex flex-col">
        {active ? (
          <>
            <header className="border-b border-border p-3"><div className="font-display text-lg font-bold">{active.title}</div></header>
            <div className="flex-1 space-y-2 overflow-auto p-4">
              {posts.map((p) => (
                <div key={p.id} className="rounded-xl border border-border bg-background/60 p-3 text-sm">
                  <div>{p.body}</div>
                  <div className="mt-1 text-[10px] text-muted-foreground">{new Date(p.created_at).toLocaleString()}</div>
                </div>
              ))}
            </div>
            <div className="flex gap-2 border-t border-border p-3">
              <Input value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Reply…" onKeyDown={(e) => { if (e.key === "Enter") postReply(); }} />
              <Button onClick={postReply}><Send className="h-4 w-4" /></Button>
            </div>
          </>
        ) : <div className="grid flex-1 place-items-center text-sm text-muted-foreground">Select a thread</div>}
      </section>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>New discussion thread</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <select value={form.stream_id} onChange={(e) => setForm({ ...form, stream_id: e.target.value })} className="rounded-lg border border-input bg-background px-3 py-2 text-sm">
              <option value="">Choose class…</option>
              {ctx.streams.map((s: any) => <option key={s.id} value={s.id}>{s.class_name} · {s.name}</option>)}
            </select>
            <Input placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            <Textarea rows={4} placeholder="Opening message" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} />
          </div>
          <DialogFooter><Button onClick={createThread}>Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
